import { debug } from '@pyranger/utils';
import type { EditorActorRef, EditorContext } from '@pyranger/types';

const textEncoder = new TextEncoder();

/**
 * These two functions update both the mutable ref value (for immediate reads)
 * and emit an XState event (for React UI reactivity).
 */

export const setIsFileRunning = ({
    actorRef,
    context,
    isFileRunning,
}: {
    actorRef: EditorActorRef;
    context: EditorContext;
    isFileRunning: boolean;
}) => {
    context.ref.current.repl.isFileRunning = isFileRunning;

    actorRef.send({
        type: 'repl.setIsFileRunning',
        value: { isFileRunning },
    });
};

export const setIsExecutingCommand = ({
    actorRef,
    context,
    isExecutingCommand,
}: {
    actorRef: EditorActorRef;
    context: EditorContext;
    isExecutingCommand: boolean;
}) => {
    context.ref.current.repl.isExecutingCommand = isExecutingCommand;

    actorRef.send({
        type: 'repl.setIsExecutingCommand',
        value: { isExecutingCommand },
    });
};

/**
 * Get the global writer or create a new one for the given port.
 */
export const getWriter = ({
    context,
    port,
}: {
    context: EditorContext;
    port: SerialPort;
}): WritableStreamDefaultWriter<Uint8Array> => {
    const { globalWriter } = context.ref.current.connection;

    if (globalWriter) {
        return globalWriter;
    }

    const nextWriter = port.writable?.getWriter() ?? null;
    if (!nextWriter) {
        throw new Error('Port is not writable');
    }

    context.ref.current.connection.globalWriter = nextWriter;
    return nextWriter;
};

/**
 * Send a command (string or Uint8Array) to the device via the serial port writer.
 * Used by the interactive terminal (keyboard input → device).
 */
export const sendCommand = async ({
    context,
    command,
    port,
}: {
    context: EditorContext;
    command: string | Uint8Array;
    port: SerialPort;
}) => {
    debug.editor(
        `Sending command: ${command instanceof Uint8Array ? 'Uint8Array' : JSON.stringify(command)}`,
    );

    // Debug: write outgoing commands to Data Out textarea (batched via rAF)
    if (context.dataOutRef?.current) {
        const text =
            command instanceof Uint8Array
                ? new TextDecoder().decode(command)
                : command;
        const device = context.ref.current.device;
        device.debugOutBuffer += text + '\n';
        if (!device.debugOutFlushScheduled) {
            device.debugOutFlushScheduled = true;
            requestAnimationFrame(() => {
                device.debugOutFlushScheduled = false;
                const el = context.dataOutRef?.current;
                if (!el) {
                    device.debugOutBuffer = '';
                    return;
                }
                if (el.value.length + device.debugOutBuffer.length > 50000) {
                    el.value = el.value.substring(10000);
                }
                el.value += device.debugOutBuffer;
                el.scrollTop = el.scrollHeight;
                device.debugOutBuffer = '';
            });
        }
    }

    const writer = getWriter({ context, port });

    try {
        if (command instanceof Uint8Array) {
            await writer.write(command);
        } else {
            await writer.write(textEncoder.encode(command));
        }
    } catch (error) {
        console.error('Write error:', error);
    }
};
